import React, { useEffect, useState } from "react";
import { Share, Plus, X, Home, WifiOff, Zap, Camera, MoreVertical } from "lucide-react";

const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
const KEY = "sp-worker-save-snooze";
const isStandalone = () =>
  (window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone) || false;

// Shown ONCE (7-day snooze) over a shared punch list, before the worker digs in,
// to offer the free "save it to your phone" install — no signup, no account talk.
export function WorkerSaveModal({ enabled, jobName }) {
  const [open, setOpen] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent || "");

  useEffect(() => {
    if (!enabled || isStandalone()) return;
    try {
      const d = Number(localStorage.getItem(KEY) || 0);
      if (d && Date.now() - d < SNOOZE_MS) return;
    } catch { /* ignore */ }
    const t = setTimeout(() => setOpen(true), 650);
    return () => clearTimeout(t);
  }, [enabled]);

  const snooze = () => {
    try { localStorage.setItem(KEY, String(Date.now())); } catch { /* ignore */ }
    setOpen(false);
  };

  const save = async () => {
    const deferred = window.__deferredInstallPrompt;
    if (deferred) {
      try {
        deferred.prompt();
        const { outcome } = await deferred.userChoice;
        window.__deferredInstallPrompt = null;
        if (outcome === "accepted") { snooze(); return; }
      } catch { /* fall through to manual steps */ }
    }
    setShowSteps(true);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[85] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center"
      data-testid="worker-save-modal"
      onClick={snooze}
    >
      <div
        className="animate-in-up w-full rounded-t-3xl border border-neutral-800 bg-[#141414] p-6 text-white sm:max-w-sm sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1.5rem)" }}
      >
        <div className="flex items-start justify-between">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500 text-black"><Home className="h-6 w-6" /></div>
          <button onClick={snooze} data-testid="worker-save-close" className="text-neutral-500 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        {!showSteps ? (
          <>
            <h3 className="mt-4 font-heading text-xl font-bold">Don't lose this list</h3>
            <p className="mt-1 text-sm text-neutral-400">
              Save {jobName ? <b className="text-neutral-200">“{jobName}”</b> : "this punch list"} to your phone — it's free and opens like an app. No account needed.
            </p>
            <ul className="mt-4 space-y-2.5 text-sm text-neutral-200">
              <li className="flex items-center gap-2.5"><Home className="h-4 w-4 shrink-0 text-emerald-400" /> One tap from your home screen — no digging through texts</li>
              <li className="flex items-center gap-2.5"><WifiOff className="h-4 w-4 shrink-0 text-emerald-400" /> Keeps working on bad job-site signal</li>
              <li className="flex items-center gap-2.5"><Camera className="h-4 w-4 shrink-0 text-emerald-400" /> Snap proof photos in seconds</li>
            </ul>
            <button
              data-testid="worker-save-start"
              onClick={save}
              className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 text-base font-bold text-black transition-transform active:scale-95"
            >
              <Plus className="h-4 w-4" strokeWidth={3} /> Save to Home Screen
            </button>
            <button data-testid="worker-save-open-list" onClick={snooze} className="mt-2 h-10 w-full text-sm font-semibold text-neutral-400 hover:text-white">
              Open the list
            </button>
          </>
        ) : (
          <>
            <h3 className="mt-4 font-heading text-xl font-bold">Add to Home Screen</h3>
            <p className="mt-1 text-sm text-neutral-400">A couple of taps and this list lives on your phone like a real app.</p>
            <ol className="mt-4 space-y-3 text-sm text-neutral-200">
              <li className="flex items-center gap-2">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-xs font-bold text-emerald-400">1</span>
                Tap the {isIOS ? <Share className="mx-1 inline h-4 w-4" /> : <MoreVertical className="mx-1 inline h-4 w-4" />} {isIOS ? "Share" : "menu"} button {isIOS ? "at the bottom" : "at the top-right"}.
              </li>
              <li className="flex items-center gap-2">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-xs font-bold text-emerald-400">2</span>
                Choose <b className="mx-1 inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add to Home Screen</b>.
              </li>
              <li className="flex items-center gap-2">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-xs font-bold text-emerald-400">3</span>
                Tap <b className="mx-1">Add</b> — done!
              </li>
            </ol>
            <button data-testid="worker-save-gotit" onClick={snooze} className="mt-5 h-12 w-full rounded-xl bg-emerald-500 text-base font-bold text-black transition-transform active:scale-95">
              Got it
            </button>
          </>
        )}
      </div>
    </div>
  );
}
